import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import GlobalLoader from "../../common/GlobalLoader";
import {
  getInitialTests,
  getInitialTestById,
  updateInitialTest,
} from "../../../helper/apicalls/initialTest";
import { getCourses } from "../../../helper/apicalls/course";
import MCQViewer from "../../common/MCQViewer";
import TrueFalseViewer from "../../common/TrueFalseViewer";
import MatchViewer from "../../common/MatchViewer";
import FillUpsViewer from "../../common/FillUpsViewer";
import AnalysisComponent from "../../common/AnalysisComponent";
import InitialTestCard from "./InitialTestCard";
import PlusCircle from "../../../styles/assets/svg/pluscircle.svg";
import ContentGeneratorPopup from "../../common/ContentGeneratorPopup";
import "../../../styles/admin/courses.scss";
import "../../../styles/common/popup.scss";

const styles = `
  .test-content-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .test-content-item {
    padding: 15px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #f9f9f9;
  }
  
  .test-content-item h3 {
    margin-top: 0;
    color: #333;
  }
`;

if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
}

const InitialTestCourseTests = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const { setHeaderName } = useOutletContext();
  const [courseName, setCourseName] = useState("");
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);

  const fetchCourseName = async () => {
    try {
      const courses = await getCourses();
      const course = Array.isArray(courses)
        ? courses.find((c) => c.key === key)
        : null;
      if (course) {
        setCourseName(course.name);
        setHeaderName(`Initial Tests - ${course.name}`);
      } else {
        setHeaderName("Initial Tests");
      }
    } catch (err) {
      console.error("Error fetching course name for initial tests:", err);
      setHeaderName("Initial Tests");
    }
  };

  const fetchTests = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getInitialTests(key);
      setTests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch initial tests:", err);
      setError("Unable to load initial tests. Please try again.");
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTest = () => {
    navigate(`/admin/initial-test/course/${key}/create`);
  };

  const handleTestClick = async (test) => {
    if (!test || !test.id) return;

    setIsPopupOpen(true);
    setSelectedLoading(true);
    setSelectedError(null);
    setSelectedTest(null);

    try {
      const data = await getInitialTestById(test.id);
      setSelectedTest(data || null);
    } catch (err) {
      console.error("Failed to fetch initial test by id:", err);
      setSelectedError("Unable to load test details. Please try again.");
    } finally {
      setSelectedLoading(false);
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedTest(null);
    setSelectedError(null);
  };

  const handleAddNextTest = () => {
    setIsGeneratorOpen(true);
  };

  const renderSelectedTestContent = () => {
    if (
      !selectedTest ||
      !Array.isArray(selectedTest.content) ||
      !selectedTest.content.length
    ) {
      return <div>No content available for this test.</div>;
    }

    return (
      <div className="test-content-container">
        {selectedTest.content.map((item, index) => {
          let contentType = item.type || "content";
          let contentToDisplay = item.content || item;

          switch (contentType) {
            case "mcq":
              return (
                <div key={index} className="test-content-item">
                  <MCQViewer
                    questionData={contentToDisplay}
                    title={`${selectedTest.name || "MCQ"} - Question ${
                      index + 1
                    }`}
                    pageId={selectedTest.id}
                    pageData={selectedTest}
                    context="initial-test"
                    contentIndex={index}
                    allowEditWithoutPersist={true}
                    onUpdate={(updated) => {
                      if (updated) {
                        setSelectedTest(updated);
                      }
                    }}
                    onAddNext={
                      index === selectedTest.content.length - 1
                        ? handleAddNextTest
                        : undefined
                    }
                  />
                </div>
              );
            case "trueorfalse":
              return (
                <div key={index} className="test-content-item">
                  <TrueFalseViewer
                    questionData={contentToDisplay}
                    title={`${selectedTest.name || "True / False"} - Question ${
                      index + 1
                    }`}
                    pageId={selectedTest.id}
                    pageData={selectedTest}
                    context="initial-test"
                    contentIndex={index}
                    allowEditWithoutPersist={true}
                    onUpdate={(updated) => {
                      if (updated) {
                        setSelectedTest(updated);
                      }
                    }}
                    onAddNext={
                      index === selectedTest.content.length - 1
                        ? handleAddNextTest
                        : undefined
                    }
                  />
                </div>
              );
            case "match":
              return (
                <div key={index} className="test-content-item">
                  <MatchViewer
                    questionData={contentToDisplay}
                    title={`${selectedTest.name || "Match"} - Question ${
                      index + 1
                    }`}
                    onAddNext={
                      index === selectedTest.content.length - 1
                        ? handleAddNextTest
                        : undefined
                    }
                  />
                </div>
              );
            case "fillups":
              return (
                <div key={index} className="test-content-item">
                  <FillUpsViewer
                    questionData={contentToDisplay}
                    title={`${selectedTest.name || "Fill Ups"} - Question ${
                      index + 1
                    }`}
                    onAddNext={
                      index === selectedTest.content.length - 1
                        ? handleAddNextTest
                        : undefined
                    }
                  />
                </div>
              );
            case "analysis":
              return (
                <div key={index} className="test-content-item">
                  <AnalysisComponent
                    data={contentToDisplay}
                    title={`${selectedTest.name || "Analysis"} - Question ${
                      index + 1
                    }`}
                    onAddNext={
                      index === selectedTest.content.length - 1
                        ? handleAddNextTest
                        : undefined
                    }
                  />
                </div>
              );
            default:
              return (
                <div key={index} className="test-content-item">
                  <div className="content-preview">
                    <h3>Content - Question {index + 1}</h3>
                    <pre>
                      {typeof contentToDisplay === "string"
                        ? contentToDisplay
                        : JSON.stringify(contentToDisplay, null, 2)}
                    </pre>
                  </div>
                </div>
              );
          }
        })}
      </div>
    );
  };

  const handleSaveGeneratedContent = async (generatedContent) => {
    if (selectedTest && generatedContent) {
      try {
        let existingContent = Array.isArray(selectedTest.content)
          ? [...selectedTest.content]
          : [];

        let newContentItem;
        try {
          const parsedContent =
            typeof generatedContent === "string"
              ? JSON.parse(generatedContent)
              : generatedContent;

          if (parsedContent.type) {
            newContentItem = parsedContent;
          } else {
            const firstItemType = selectedTest.content[0]?.type || "mcq";
            newContentItem = {
              type: firstItemType,
              content: parsedContent,
            };
          }
        } catch (parseError) {
          const firstItemType = selectedTest.content[0]?.type || "mcq";
          newContentItem = {
            type: firstItemType,
            content: generatedContent,
          };
        }

        const lastItem = existingContent[existingContent.length - 1];
        const nextSequence =
          lastItem && typeof lastItem.sequence === "number"
            ? lastItem.sequence + 1
            : existingContent.length + 1;

        newContentItem.sequence = nextSequence;

        existingContent.push(newContentItem);

        const updatedTest = {
          ...selectedTest,
          content: existingContent,
        };

        await updateInitialTest(selectedTest.id, updatedTest);

        setSelectedTest(updatedTest);

        fetchTests();

        setIsGeneratorOpen(false);
      } catch (error) {
        console.error("Error saving generated content:", error);
        setSelectedError("Failed to save generated content. Please try again.");
      }
    }
  };

  useEffect(() => {
    fetchCourseName();
    fetchTests();
  }, [key]);
  
  return (
    <div className="courses-container">
      <GlobalLoader visible={loading} text="Loading initial tests..." />

      <div className="courses-header-actions">
        <h2 style={{ margin: 0 }}>
          {courseName ? `Initial Tests - ${courseName}` : "Initial Tests"}
        </h2>
      </div>

      <div className="courses-grid">
        <div className="course-card add-course-card" onClick={handleAddTest}>
          <div className="course-card-body">
            <img src={PlusCircle} alt="Add Test" className="add-course-icon" />
            <h3 className="course-title">Add Test</h3>
          </div>
        </div>

        {!error &&
          tests.map((test) => (
            <InitialTestCard
              key={test.id || test.key || test.name}
              test={test}
              onClick={handleTestClick}
            />
          ))}
      </div>

      {error && (
        <div className="loading-container">
          <div>{error}</div>
        </div>
      )}

      {isPopupOpen && (
        <div
          className="common-popup-overlay high-z-index"
          onClick={handleClosePopup}
        >
          <div
            className="common-popup-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-header with-close">
              <h2>{selectedTest?.name || "Test Details"}</h2>
              <button className="popup-close-button" onClick={handleClosePopup}>
                ×
              </button>
            </div>

            <div className="popup-content">
              {selectedLoading && <div>Loading test details...</div>}
              {selectedError && (
                <div className="popup-error-message">{selectedError}</div>
              )}
              {!selectedLoading &&
                !selectedError &&
                selectedTest &&
                renderSelectedTestContent()}
            </div>
          </div>
        </div>
      )}

      {isGeneratorOpen && (
        <ContentGeneratorPopup
          open={isGeneratorOpen}
          handleClose={() => setIsGeneratorOpen(false)}
          onSave={handleSaveGeneratedContent}
          pageData={selectedTest || null}
          context="initial-test"
          courseKey={key}
        />
      )}
    </div>
  );
};

export default InitialTestCourseTests;
